import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { CardVariant } from './m-card.component.types';
import { Card } from 'primeng/card';
import { NgClass, NgStyle } from '@angular/common';
import { PrimeTemplate } from 'primeng/api';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-card',
  templateUrl: './m-card.component.html',
  styleUrls: ['./m-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [Card, NgClass, NgStyle, PrimeTemplate]
})
export class MCardComponent implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @Input() draggable = false;
  @Input() hoverable = false;
  @Input() showIcon = false;
  @Input() noShadow = false;
  @Input() noBorder = false;
  @Input() scrollable = false;
  @Input() backgroundColor?: string;
  @Input() variant: CardVariant = CardVariant.standard;
  @Input() styleClass?: string;

  hovered = false;
  dragging = false;

  get cardClasses(): Record<string, boolean> {
    return {
      hover: this.isHovered,
      'small-card': this.variant === 'small',
      compact: this.variant === 'compact',
      draggable: this.draggable,
      dragging: this.dragging,
      'no-shadow': this.noShadow,
      'no-border': this.noBorder,
      scrollable: this.scrollable
    };
  }

  get cardStyles(): Record<string, string> {
    const styles: Record<string, string> = {};
    if (this.backgroundColor) {
      styles['background-color'] = this.backgroundColor;
    }
    return styles;
  }

  get showDragIcon(): boolean {
    return this.draggable && (this.hovered || this.dragging);
  }

  get showHoverIcon(): boolean {
    return this.hoverable && this.hovered && this.showIcon;
  }

  get isHovered(): boolean {
    return this.hovered && this.hoverable;
  }

  // Hover state is tracked via native listeners attached outside the Angular
  // zone so that hovering a card never schedules an application-wide change
  // detection pass; a local detectChanges() re-renders just this card. Cards
  // that consume neither the hover nor drag state skip registration entirely.
  ngOnInit(): void {
    if (!this.hoverable && !this.draggable) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const element = this.elementRef.nativeElement;
      element.addEventListener('mouseenter', this.handleMouseEnter);
      element.addEventListener('mouseleave', this.handleMouseLeave);
    });
  }

  ngOnDestroy(): void {
    const element = this.elementRef.nativeElement;
    element.removeEventListener('mouseenter', this.handleMouseEnter);
    element.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  @HostListener('mousedown')
  onMouseDown(): void {
    if (this.draggable) {
      this.dragging = true;
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.dragging = false;
  }

  private readonly handleMouseEnter = (): void => {
    this.hovered = true;
    this.changeDetectorRef.detectChanges();
  };

  private readonly handleMouseLeave = (): void => {
    this.hovered = false;
    this.dragging = false;
    this.changeDetectorRef.detectChanges();
  };
}
